import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useNavigate } from "react-router-dom";

import { isSidebarMenuOpen as getIsSidebarMenuOpen, isGuestAccountActive } from "@awe-web/shared/lib";
import { switchToUser } from "../user/actions.js";
import { getAuthorizedUsers, getCurrentUserId, getProfileScreenLink } from "../user/store";

export default function UserProfileLink({ showAsActive }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const currentUserId = useSelector(getCurrentUserId);
  const authorizedUsers = useSelector((state) => getAuthorizedUsers(state));
  const isSidebarMenuOpen = useSelector((state) => getIsSidebarMenuOpen(state));
  const stateProfileScreenLink = useSelector((state) => getProfileScreenLink(state));

  const isGuestAccount = useSelector(state => isGuestAccountActive(state));
  const profileScreenLink = useMemo(() => stateProfileScreenLink.toJS(), [stateProfileScreenLink]);
  const hasAuthorizedUsers = useMemo(() => authorizedUsers.toJS().length > 1, [authorizedUsers]);

  const onLinkClick = useCallback((event) => {
    if (isSidebarMenuOpen && hasAuthorizedUsers) {
      // Prevents default behavior of sidebar panel to keep it open while select options are open
      if (event.preventDefault && typeof event.preventDefault === "function") {
        event.preventDefault();
      }
    }
  }, [hasAuthorizedUsers, isSidebarMenuOpen]);

  const onAuthorizedUserChange = useCallback(async (event) => {
    if (isNaN(event.target.value)) {
      return;
    }
    const userId = Number(event.target.value);
    event.target.value = -1;

    const nextPath = await dispatch(switchToUser(userId));

    if (nextPath && nextPath.length > 0) {
      navigate(nextPath);
    }
  }, [dispatch, navigate]);

  const linkClassNames = useMemo(() => {
    let result = [
      "user-profile-link__link user-navigation__link",
      "user-navigation__link--profile"
    ];

    if (showAsActive) {
      result = result.concat([
        "user-profile-link__link--active",
        "user-navigation__link--profile--active"
      ]);
    }
    return result.join(' ');
  }, [showAsActive]);

  const userSelection = useCallback(() => {
    if (isSidebarMenuOpen && hasAuthorizedUsers) {
      return (
        <select
          className="user-profile-link__select"
          onChange={onAuthorizedUserChange}
          value={-1}
        >
          <option key={-1} value={-1} disabled />
          {authorizedUsers.toJS().map((authorizedUser) => (
            <option key={authorizedUser.id} value={authorizedUser.id}>
              {authorizedUser.label}
            </option>
          ))}
        </select>
      );
    } else {
      return null;
    }
  }, [authorizedUsers, hasAuthorizedUsers, isSidebarMenuOpen, onAuthorizedUserChange]);

  const usersOverlay = useCallback(() => {
    if (!isGuestAccount && !isSidebarMenuOpen && hasAuthorizedUsers) {
      return (
        <div className="user-profile-link__overlay">
          <ul className="user-profile-link__authorized-users">
            {authorizedUsers.toJS()
              .filter((authorizedUser) => authorizedUser.id !== currentUserId)
              .map((authorizedUser) => (
                <li
                  key={authorizedUser.id}
                  className="user-profile-link__authorized-user"
                >
                  <button
                    className="user-profile-link__authorized-user-button"
                    onClick={() => onAuthorizedUserChange({
                      target: { value: authorizedUser.id }
                    })}
                  >
                    {authorizedUser.label}
                  </button>
                </li>
              ))}
          </ul>
        </div>
      );
    } else {
      return null;
    }
  }, [authorizedUsers, currentUserId, hasAuthorizedUsers, isGuestAccount, isSidebarMenuOpen, onAuthorizedUserChange]);

  if (isGuestAccount) {
    return null;
  }
  else return (
    <div className="user-profile-link">
      <NavLink
        className={linkClassNames}
        to={profileScreenLink.path}
        onClick={onLinkClick}
      >
        {userSelection()}
        {profileScreenLink.label}
      </NavLink>

      {usersOverlay()}
    </div>
  );
}
